import { z } from 'zod';

import { CURRENCIES } from '../constants/currency';
import { CEFR_LEVELS } from '../constants/languages';
import { IT_VERTICALS } from '../constants/verticals';
import { cuidLike, isoLanguageCode, nonEmptyString, paginationInput } from '../zod-common';

export const OFFER_MODES = ['TIME_BASED_DAY', 'TIME_BASED_HOUR', 'FIXED', 'PER_LINE'] as const;
export type OfferModeValue = (typeof OFFER_MODES)[number];

export const OFFER_SIZES = ['POC', 'MEDIUM', 'LARGE', 'LONG_TERM'] as const;
export type OfferSizeValue = (typeof OFFER_SIZES)[number];

export const BIDDER_PREFERENCES = ['INDIVIDUAL', 'COMPANY', 'ANY'] as const;
export type BidderPreferenceValue = (typeof BIDDER_PREFERENCES)[number];

export const OFFER_STATUSES = [
  'DRAFT',
  'PUBLISHED',
  'SELECTION_IN_PROGRESS',
  'AWAITING_PAYMENT',
  'PAYMENT_ISSUE',
  'ACTIVE',
  'EXPIRED_NO_SELECTION',
  'CLOSED_NO_WINNER',
  'ARCHIVED',
] as const;
export type OfferStatusValue = (typeof OFFER_STATUSES)[number];

export const offerLanguageSchema = z.object({
  code: isoLanguageCode,
  requiredLevel: z.enum(CEFR_LEVELS),
});
export type OfferLanguage = z.infer<typeof offerLanguageSchema>;

export const secondaryLanguagesSchema = z
  .array(offerLanguageSchema)
  .max(2, 'Max 2 langues secondaires')
  .default([])
  .refine((langs) => new Set(langs.map((l) => l.code)).size === langs.length, 'Doublons interdits');

const titleSchema = nonEmptyString.max(180, 'Max 180 caractères');
const descriptionSchema = z.string().trim().max(8_000, 'Max 8000 caractères');

export const createDraftOfferSchema = z.object({
  title: titleSchema,
  description: descriptionSchema.default(''),
  vertical: z.enum(IT_VERTICALS),
  size: z.enum(OFFER_SIZES).optional().nullable(),
  pricingPreference: z.enum(OFFER_MODES).optional().nullable(),
  bidderPreference: z.enum(BIDDER_PREFERENCES).default('ANY'),
  primaryLanguage: offerLanguageSchema,
  secondaryLanguages: secondaryLanguagesSchema,
  budgetAmount: z
    .number()
    .nonnegative('Doit être ≥ 0')
    .max(1_000_000_000, 'Montant trop élevé')
    .optional()
    .nullable(),
  budgetCurrency: z.enum(CURRENCIES).optional().nullable(),
  applicationDeadline: z.date().optional().nullable(),
  isPrivate: z.boolean().default(false),
});
export type CreateDraftOfferInput = z.infer<typeof createDraftOfferSchema>;

export const updateOfferSchema = createDraftOfferSchema.partial().extend({
  id: cuidLike,
});
export type UpdateOfferInput = z.infer<typeof updateOfferSchema>;

export const EXPECTATION_TITLE_MAX = 180;
export const EXPECTATION_DESCRIPTION_MAX = 2_000;
export const EXPECTATION_MAX_CHILDREN = 50;
export const EXPECTATION_MAX_MODULES = 30;

export const expectationChildSchema = z.object({
  id: z.string().max(64).optional(),
  title: nonEmptyString.max(EXPECTATION_TITLE_MAX, `Max ${EXPECTATION_TITLE_MAX} caractères`),
  description: z
    .string()
    .trim()
    .max(EXPECTATION_DESCRIPTION_MAX, `Max ${EXPECTATION_DESCRIPTION_MAX} caractères`)
    .default(''),
  order: z.number().int().min(0),
  isUserEdited: z.boolean().default(false),
});
export type ExpectationChildInput = z.infer<typeof expectationChildSchema>;

export const expectationParentSchema = z.object({
  id: z.string().max(64).optional(),
  title: nonEmptyString.max(EXPECTATION_TITLE_MAX, `Max ${EXPECTATION_TITLE_MAX} caractères`),
  description: z
    .string()
    .trim()
    .max(EXPECTATION_DESCRIPTION_MAX, `Max ${EXPECTATION_DESCRIPTION_MAX} caractères`)
    .default(''),
  order: z.number().int().min(0),
  isUserEdited: z.boolean().default(false),
  children: z
    .array(expectationChildSchema)
    .max(EXPECTATION_MAX_CHILDREN, `Max ${EXPECTATION_MAX_CHILDREN} sous-attentes`)
    .default([]),
});
export type ExpectationParentInput = z.infer<typeof expectationParentSchema>;

export const expectationsTreeSchema = z
  .array(expectationParentSchema)
  .max(EXPECTATION_MAX_MODULES, `Max ${EXPECTATION_MAX_MODULES} modules`)
  .superRefine((tree, ctx) => {
    const seenIds = new Set<string>();
    for (const parent of tree) {
      for (const id of [parent.id, ...parent.children.map((child) => child.id)]) {
        if (!id) continue;
        if (seenIds.has(id)) {
          ctx.addIssue({ code: 'custom', message: 'DUPLICATE_EXPECTATION_ID' });
          return;
        }
        seenIds.add(id);
      }
    }
  });
export type ExpectationsTreeInput = z.infer<typeof expectationsTreeSchema>;

export function clampExpectationsTree(tree: ExpectationParentInput[]): ExpectationParentInput[] {
  return tree.slice(0, EXPECTATION_MAX_MODULES).map((parent, parentIndex) => ({
    ...parent,
    title: parent.title.slice(0, EXPECTATION_TITLE_MAX),
    description: (parent.description ?? '').slice(0, EXPECTATION_DESCRIPTION_MAX),
    order: parentIndex,
    children: (parent.children ?? [])
      .slice(0, EXPECTATION_MAX_CHILDREN)
      .map((child, childIndex) => ({
        ...child,
        title: child.title.slice(0, EXPECTATION_TITLE_MAX),
        description: (child.description ?? '').slice(0, EXPECTATION_DESCRIPTION_MAX),
        order: childIndex,
      })),
  }));
}

export const upsertExpectationsSchema = z.object({
  offerId: cuidLike,
  expectations: expectationsTreeSchema,
});
export type UpsertExpectationsInput = z.infer<typeof upsertExpectationsSchema>;

export const publishOfferSchema = z.object({
  offerId: cuidLike,
});
export type PublishOfferInput = z.infer<typeof publishOfferSchema>;

export const archiveOfferSchema = z.object({
  offerId: cuidLike,
});
export type ArchiveOfferInput = z.infer<typeof archiveOfferSchema>;

export const duplicateOfferSchema = z.object({
  offerId: cuidLike,
  applicationDeadline: z
    .date()
    .refine((value) => value.getTime() > Date.now(), 'DEADLINE_MUST_BE_FUTURE'),
});
export type DuplicateOfferInput = z.infer<typeof duplicateOfferSchema>;

export const deleteOfferSchema = z.object({
  offerId: cuidLike,
});
export type DeleteOfferInput = z.infer<typeof deleteOfferSchema>;

export const offerInviteeSchema = z
  .string()
  .trim()
  .min(2, 'Identifiant trop court')
  .max(200, 'Identifiant trop long');

export const invitePrivateOfferSchema = z.object({
  offerId: cuidLike,
  invitees: z
    .array(offerInviteeSchema)
    .min(1, 'Au moins un invité')
    .max(50, 'Max 50 invités à la fois'),
});
export type InvitePrivateOfferInput = z.infer<typeof invitePrivateOfferSchema>;

export const editPublishedOfferSchema = z.object({
  offerId: cuidLike,
  title: titleSchema.optional(),
  description: descriptionSchema.optional(),
  primaryLanguage: offerLanguageSchema.optional(),
  secondaryLanguages: secondaryLanguagesSchema.optional(),
  budgetAmount: z
    .number()
    .nonnegative('Doit être ≥ 0')
    .max(1_000_000_000, 'Montant trop élevé')
    .optional()
    .nullable(),
  budgetCurrency: z.enum(CURRENCIES).optional().nullable(),
  applicationDeadline: z.date().optional().nullable(),
  bidderPreference: z.enum(BIDDER_PREFERENCES).optional(),
  pricingPreference: z.enum(OFFER_MODES).optional().nullable(),
  expectations: expectationsTreeSchema,
  reason: z.string().trim().max(500).default(''),
  forceMinor: z.boolean().default(false),
});
export type EditPublishedOfferInput = z.infer<typeof editPublishedOfferSchema>;

export const offerByIdSchema = z.object({
  offerId: cuidLike,
});
export type OfferByIdInput = z.infer<typeof offerByIdSchema>;

export const offerVersionHistorySchema = z.object({
  offerId: cuidLike,
});
export type OfferVersionHistoryInput = z.infer<typeof offerVersionHistorySchema>;

export const offerVersionSnapshotSchema = z.object({
  offerId: cuidLike,
  snapshotId: cuidLike,
});
export type OfferVersionSnapshotInput = z.infer<typeof offerVersionSnapshotSchema>;

export const listOffersSchema = paginationInput.extend({
  status: z.enum(OFFER_STATUSES).optional(),
});
export type ListOffersInput = z.infer<typeof listOffersSchema>;
